/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ТРАНСПОРТ: axios-инстанс, оба интерцептора и single-flight refresh
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Слой `lib`. Правило зависимостей — `types ← lib ← api ← stores ← views` —
 * означает, что **этот файл не имеет права импортировать ни один стор**.
 * Импорт `stores/auth` отсюда даёт цикл: стор зовёт API, API зовёт `http`,
 * `http` зовёт стор. Связь идёт сверху вниз, через инъекцию из `main.ts`:
 * `setAuthLostHandler()` и `setTokensRotatedHandler()` (см. §2.3 плана).
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПОЧЕМУ ДВА ИНСТАНСА
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `http` — рабочий, с обоими интерцепторами.
 * `bare` — **только** для `POST /api/v1/auth/refresh`, без интерцепторов вообще.
 *
 * Это не микрооптимизация, а защита от бесконечной рекурсии. Refresh-токен
 * тоже протухает и тоже отзывается, то есть `/auth/refresh` умеет отвечать 401.
 * Уйди этот запрос через `http`, его 401 поймал бы response-интерцептор,
 * который в ответ на 401 делает… refresh. Чтобы сделать refresh. И так далее,
 * пока не кончится стек или терпение сервера.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПОЧЕМУ SINGLE-FLIGHT — ЭТО НЕ ОПТИМИЗАЦИЯ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Сервер ротирует refresh-токен на каждое использование** (§1.4 плана):
 * предъявленный токен немедленно становится `previous_token_hash`, а в ответе
 * приезжает новый. Два параллельных `POST /auth/refresh` — это гонка, в которой
 * второй предъявляет уже погашенный токен. Спасает только 30-секундное окно
 * «легальной гонки вкладок» на сервере, и оно задумано как страховка, а не как
 * разрешение фанаутить: за его пределами то же самое читается как **компрометация**
 * (`REFRESH_TOKEN_REUSED`), сессия гасится, пользователя выкидывает.
 *
 * Отсюда очередь. И вся очередь — это одна переменная:
 *
 *     let inFlight: Promise<string> | null
 *
 * Первый дошедший до `refreshOnce()` создаёт промис, остальные девять находят
 * его непустым и **ждут тот же объект**. Из браузера уходит ровно один запрос;
 * когда он оседает, резолв промиса сам раздаётся всем ожидающим, и каждый
 * переигрывает свой собственный запрос. Массив ожидающих не нужен, порядок
 * пробуждения не важен, потерять ожидающего негде — промис и есть очередь.
 *
 * `refreshOnce()` экспортируется наружу именно затем, чтобы `auth.bootstrap()`
 * (§2.6) переиспользовал **этот же** промис, а не заводил вторую ротацию рядом.
 */

import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

import { ApiError, normalizeError } from '@/lib/api-error'
import { tokenStorage } from '@/lib/token-storage'
import type { RefreshRequest, RefreshResponse, TokenPair, User } from '@/types/api'

/**
 * Два поля конфига, которых у axios нет. Аугментация модуля, а не свой тип-обёртка:
 * иначе каждый вызов с `skipAuth` требовал бы приведения на месте.
 *
 * Параметры `D`/`P` обязаны повторять исходное объявление
 * (`AxiosRequestConfig<D = any, P = any>`) — TS требует идентичных списков
 * параметров у всех деклараций одного интерфейса.
 */
declare module 'axios' {
  export interface AxiosRequestConfig<D = any, P = any> {
    /**
     * Не подставлять `Authorization`. Для публичных операций контракта:
     * `Login`, `Refresh`, `Logout` (§1.2 плана).
     *
     * У флага есть второй, менее очевидный эффект: помеченный им запрос
     * **никогда не триггерит refresh на 401**. Это существенно — 401 на логине
     * с неверным паролем (`INVALID_CREDENTIALS`) иначе запускал бы ротацию
     * токенов вообще не вошедшего пользователя.
     */
    skipAuth?: boolean

    /**
     * Запрос уже переигрывали после refresh. Ставится интерцептором,
     * вручную не выставляется.
     *
     * Без этого флага сценарий «refresh прошёл, но сессия всё равно мертва»
     * (например, её погасили из другой вкладки между двумя запросами) даёт
     * бесконечный цикл 401 → refresh → 401 → refresh.
     */
    _retried?: boolean
  }
}

/* ═════════════════════════════════════════════════════════════════════════
   1. ИНСТАНСЫ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * `POST /api/v1/auth/refresh` — вынесен в константу, потому что путь нужен
 * в двух местах: здесь (голый инстанс, внутренняя ротация) и в `api/auth.ts`
 * (публичная обёртка для стора). Две строковые копии рано или поздно разъедутся.
 */
export const AUTH_REFRESH_PATH = '/api/v1/auth/refresh'

/**
 * `baseURL` пустой — и это осознанно, а не забыто.
 *
 * Пути в `google.api.http` на бэкенде уже начинаются с `/api/v1`, поэтому
 * модули `api/*` зовут их целиком, прокси Vite ничего не переписывает (§2.2),
 * а в проде nginx отдаёт `dist/` на `/` и проксирует `/api` — те же самые
 * относительные пути работают без единой правки. Заодно это оставляет открытой
 * дорогу к httpOnly-кукам: через прокси запросы same-origin.
 *
 * ⚠️ Именно поэтому здесь **не** читается `import.meta.env.VITE_API_BASE_URL`:
 * его текущее значение `/api` склеилось бы с `/api/v1/...` в `/api/api/v1/...`
 * (axios считает абсолютным только URL со схемой или `//`).
 */
const BASE_URL = ''

/**
 * 15 с. Серверный `server.http.timeout` — 10 с (argon2 в 1 с не влезает,
 * §1.5 плана), так что клиентский порог должен быть заметно выше, иначе
 * axios оборвёт запрос раньше, чем сервер успеет ответить своей ошибкой,
 * и вместо внятного `WRONG_CURRENT_PASSWORD` пользователь увидит таймаут.
 */
const TIMEOUT_MS = 15_000

export const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT_MS,
})

/**
 * Инстанс для самого refresh. Ни одного интерцептора — см. шапку файла.
 * `Authorization` ему не нужен: `Refresh` публичный, аутентификация в теле.
 */
const bare: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT_MS,
})

/* ═════════════════════════════════════════════════════════════════════════
   2. ИНЪЕКТИРУЕМЫЕ ХЕНДЛЕРЫ
   ═════════════════════════════════════════════════════════════════════════ */

/** Сессия потеряна безвозвратно: чистить стор и уходить на `/login`. */
type AuthLostHandler = (error: ApiError) => void

/**
 * Токены отротировались. `user` приезжает вместе с ними (`RefreshResponse.user`)
 * и **перечитан из БД** — если роль сменили, здесь она уже новая, тогда как
 * claim в старом access-токене врал бы до 15 минут.
 */
type TokensRotatedHandler = (tokens: TokenPair, user: User | null) => void

/**
 * Дефолты — no-op, а не `null`. Транспорт обязан работать и до того, как
 * `main.ts` соберёт мост: моки, тесты и первый запрос на старте приложения
 * не должны падать на `onAuthLost is not a function`.
 */
let onAuthLost: AuthLostHandler = () => {}
let onTokensRotated: TokensRotatedHandler = () => {}

/** Вызывается из `wireAuthBridge()` в `main.ts` после `app.use(createPinia())`. */
export function setAuthLostHandler(handler: AuthLostHandler): void {
  onAuthLost = handler
}

/** Вызывается из `wireAuthBridge()` в `main.ts` после `app.use(createPinia())`. */
export function setTokensRotatedHandler(handler: TokensRotatedHandler): void {
  onTokensRotated = handler
}

/**
 * Хендлеры пишет другой слой, и уронить транспорт своей ошибкой он не должен:
 * исключение из `onAuthLost` внутри `catch` подменило бы собой настоящую
 * причину провала refresh, и в консоли осталась бы жалоба роутера вместо
 * `REFRESH_TOKEN_REUSED`.
 */
function emit(fn: () => void): void {
  try {
    fn()
  } catch (err) {
    console.error('[http] обработчик моста авторизации бросил исключение', err)
  }
}

/* ═════════════════════════════════════════════════════════════════════════
   3. SINGLE-FLIGHT REFRESH
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Очередь. Не массив, не флаг, не мьютекс — **промис**.
 * `null` ⇒ ротация не идёт. Не-`null` ⇒ идёт ровно одна, и вот её результат.
 */
let inFlight: Promise<string> | null = null

/**
 * Считать ли провал refresh потерей сессии.
 *
 * `true` только на 4xx: сервер ответил и ответил «нет» — токен недействителен,
 * истёк, переиспользован, сессия отозвана, пользователь отключён. Это
 * авторитетный отказ, лечится только новым логином.
 *
 * `false` на 5xx, обрыве и таймауте, и это важнее, чем кажется. Бэкенд
 * перезапустился на две секунды — если бы мы чистили хранилище здесь,
 * пользователь с живым 30-дневным refresh-токеном получил бы `/login` и
 * потерял бы сессию **безвозвратно**, потому что токен уже стёрт с диска.
 * Транспортная неудача обязана оставаться восстановимой: план прямо требует
 * «остановить бэкенд → тост, поднять обратно → восстановление без перезагрузки».
 */
function isSessionLost(error: ApiError): boolean {
  return error.status >= 400 && error.status < 500
}

/** Единственное место, где сессия объявляется мёртвой. */
function abandonSession(error: ApiError): void {
  tokenStorage.clear()
  emit(() => onAuthLost(error))
}

/**
 * Тело ротации. Выполняется **строго один раз на flight**, поэтому и чистка
 * хранилища, и `onAuthLost` здесь срабатывают ровно один раз — сколько бы
 * запросов ни ждало результата. Будь эта логика в response-интерцепторе,
 * десять одновременных 401 дали бы десять редиректов на `/login`.
 */
async function performRefresh(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken()

  if (!refreshToken) {
    // Ни access, ни refresh — предъявлять нечего. Отдельный `reason`, а не
    // выдуманный 401 с провода: холодный старт неаутентифицированного
    // пользователя это штатный путь, и он не должен выглядеть как сбой.
    const error = new ApiError(401, 'NO_REFRESH_TOKEN')
    abandonSession(error)
    throw error
  }

  try {
    const body: RefreshRequest = { refreshToken }
    const { data } = await bare.post<RefreshResponse>(AUTH_REFRESH_PATH, body)

    // `rememberMe` не передаём: при ротации хранилище остаётся тем, которое
    // выбрал логин (см. `token-storage.ts`).
    tokenStorage.save(data.tokens)
    emit(() => onTokensRotated(data.tokens, data.user ?? null))

    return data.tokens.accessToken
  } catch (err) {
    const error = normalizeError(err)
    if (isSessionLost(error)) abandonSession(error)
    throw error
  }
}

/**
 * Единственная точка входа в ротацию — и для интерцептора, и для стора.
 *
 * Идемпотентна в пределах одного flight: сколько бы раз её ни позвали, пока
 * запрос в полёте, наружу уйдёт один и тот же промис и один HTTP-запрос.
 * Ровно поэтому `auth.bootstrap()` зовёт её, а не свой `authApi.refresh()`:
 * холодный старт и параллельный 401 из зависшего компонента обязаны
 * схлопнуться в одну ротацию, иначе вторая предъявит уже погашенный токен.
 *
 * `.finally()` снимает флаг **до** того, как ожидающие возобновятся
 * (колбэк `finally` встаёт в очередь микрозадач первым), так что следующая
 * волна 401 корректно заводит новый flight.
 *
 * @returns свежий access-токен
 * @throws {ApiError} — при провале ротации; хранилище к этому моменту уже
 *   вычищено и `onAuthLost` вызван, **если** отказ был авторитетным (4xx).
 */
export function refreshOnce(): Promise<string> {
  inFlight ??= performRefresh().finally(() => {
    inFlight = null
  })

  return inFlight
}

/* ═════════════════════════════════════════════════════════════════════════
   4. REQUEST-ИНТЕРЦЕПТОРЫ
   ═════════════════════════════════════════════════════════════════════════ */

/** Методы, у которых в контракте объявлен `body`. */
const BODY_METHODS: ReadonlySet<string> = new Set(['post', 'put', 'patch'])

/**
 * ⚠️ ЖИВАЯ МИНА КОНТРАКТА: кратос требует `Content-Type: application/json` на
 * **каждом** запросе с аннотацией `body` — включая те, чьё тело пустое.
 *
 *     http.post('/api/v1/auth/sessions/revoke-others')      → 400 CODEC
 *     http.post('/api/v1/auth/sessions/revoke-others', {})  → 200 OK
 *
 * Без второго аргумента axios не проставляет заголовок вообще (ставить его
 * нечему: `transformRequest` смотрит на тело), декодер кратоса не находит
 * кодек и отвечает `400 CODEC` — причиной, которой нет в нашем enum, так что
 * диагностика выходит особенно мутной.
 *
 * Модули `api/*` и так передают `{}` явно — это документировано в `types/api.ts`.
 * Но «все всегда помнят» плохо масштабируется на полгода и трёх авторов,
 * поэтому здесь стоит структурная страховка: у POST/PUT/PATCH тело есть всегда.
 * `DELETE` не трогаем — у DELETE-операций контракта аннотации `body` нет.
 */
http.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toLowerCase()
  if (BODY_METHODS.has(method) && config.data === undefined) {
    config.data = {}
  }
  return config
})

/**
 * `Authorization: Bearer <access>`.
 *
 * Токен читается из хранилища **на каждом вызове**, а не захватывается в
 * замыкание. Это не мелочь: переигранный после ротации запрос проходит через
 * этот же интерцептор заново и подхватывает уже новый токен — поэтому
 * response-интерцептору не приходится править заголовок руками.
 */
http.interceptors.request.use((config) => {
  if (config.skipAuth === true) return config

  const accessToken = tokenStorage.getAccessToken()
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`)
  }

  return config
})

/* ═════════════════════════════════════════════════════════════════════════
   5. RESPONSE-ИНТЕРЦЕПТОР
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Стоит ли по этой ошибке пытаться обновить токен.
 *
 * **Только 401.** 403 (`FORBIDDEN`) означает «аутентифицирован, но не положено»
 * — у viewer'а нет права, и никакой новый токен этого не изменит: роль в нём
 * будет та же самая. Ротация на 403 — это лишний запрос, лишняя ротация
 * refresh-токена и замаскированная ошибка прав, которую разработчик потом
 * ищет в сетевой панели.
 */
function shouldRefresh(
  status: number | undefined,
  config: InternalAxiosRequestConfig | undefined,
): config is InternalAxiosRequestConfig {
  if (status !== 401 || config === undefined) return false
  if (config.skipAuth === true) return false
  if (config._retried === true) return false
  return true
}

http.interceptors.response.use(
  (response) => response,
  async (err: unknown) => {
    const axiosError = axios.isAxiosError(err) ? err : null
    const config = axiosError?.config
    const status = axiosError?.response?.status

    if (!shouldRefresh(status, config)) {
      // Всё, что не «протух access»: 4xx по делу, 5xx, сеть, таймаут, отмена.
      // Наружу из транспорта выходит только `ApiError` — см. `api-error.ts`.
      throw normalizeError(err)
    }

    // Метка ставится ДО await: она должна попасть на конфиг раньше, чем
    // запрос уйдёт во второй раз, иначе цикл 401 → refresh → 401 не разомкнётся.
    // `mergeConfig` в axios переносит неизвестные ключи, так что флаг переживает
    // повторную диспетчеризацию.
    config._retried = true

    try {
      // Здесь схлопывается вся волна: десять 401 — один этот вызов внутри.
      await refreshOnce()
    } catch (refreshErr) {
      // Хранилище уже вычищено и `onAuthLost` вызван внутри `performRefresh()`
      // — ровно один раз на волну. Здесь остаётся только отдать причину наверх,
      // чтобы вызывающий код не показал успех несостоявшегося запроса.
      throw normalizeError(refreshErr)
    }

    // Свежий `Authorization` подставит request-интерцептор: `http(config)` —
    // это полноценная новая диспетчеризация, а не «досыл» старой.
    return http(config)
  },
)
